<template>
  <div
    class="sidebar-column"
    :class="{ resizing: resizePointerId != null }"
    :style="{ '--sidebar-user-width': `${sidebarWidth}px` }"
  >
    <aside
      ref="sidebarElement"
      class="knowledge-sidebar"
      @scroll="scheduleSidebarScrollSave"
    >
      <template v-if="session.isAuthenticated">
        <div
          v-if="workspace.workspace && workspace.workspace.projects.length > 0"
          class="sidebar-toolbar"
          role="toolbar"
          aria-label="Project tree controls"
        >
          <span class="sidebar-toolbar-title">Projects</span>
          <div class="sidebar-toolbar-actions">
            <button
              type="button"
              class="icon-button"
              title="Collapse all"
              aria-label="Collapse all projects and folders"
              @click="workspace.setWorkspaceTreeExpanded(false)"
            >
              <ChevronsDownUp class="icon" />
            </button>
            <button
              type="button"
              class="icon-button"
              title="Expand all"
              aria-label="Expand all projects and folders"
              @click="workspace.setWorkspaceTreeExpanded(true)"
            >
              <ChevronsUpDown class="icon" />
            </button>
          </div>
        </div>
      <div v-if="currentError" class="sidebar-error">{{ currentError }}</div>
      <div v-if="!workspace.workspace" class="sidebar-empty">Loading...</div>
      <div v-else-if="workspace.workspace.projects.length === 0" class="sidebar-empty">
        <template v-if="canCreateProject">
          <p>No projects yet.</p>
          <button type="button" class="btn btn-primary" @click="beginCreateProject">
            <Plus class="icon" />
            <span>Create your first project</span>
          </button>
        </template>
        <p v-else>No projects yet - ask an administrator to grant you access.</p>
      </div>
      <nav v-else class="tree">
        <section v-for="project in workspace.workspace.projects" :key="project.id" class="tree-project">
          <div
            :class="[
              'tree-row tree-project-row has-row-actions',
              { 'drag-over': dragOverProjectId === project.id, 'has-edit-actions': project.canEdit }
            ]"
            @dragenter.prevent="dragOverProjectId = project.id"
            @dragover.prevent
            @dragleave="dragOverProjectId = null"
            @drop.prevent="dropOnProjectRoot(project)"
          >
            <button
              type="button"
              :class="['tree-twisty', { expanded: project.isExpanded }]"
              :title="project.isExpanded ? 'Collapse project' : 'Expand project'"
              @click="toggleProject(project)"
            >
              <ChevronRight class="icon" />
            </button>
            <button type="button" class="tree-label" @click="toggleProject(project)">
              <BookOpen class="icon tree-icon" />
              <span class="tree-name">{{ project.name }}</span>
            </button>
            <div class="tree-row-actions">
              <div class="tree-expansion-actions" :aria-label="`Expansion controls for ${project.name}`" role="group">
                <button
                  type="button"
                  class="icon-button"
                  title="Collapse project and descendants"
                  aria-label="Collapse project and descendants"
                  @click.stop="workspace.setProjectTreeExpanded(project.id, false)"
                >
                  <ChevronsDownUp class="icon" />
                </button>
                <button
                  type="button"
                  class="icon-button"
                  title="Expand project and descendants"
                  aria-label="Expand project and descendants"
                  @click.stop="workspace.setProjectTreeExpanded(project.id, true)"
                >
                  <ChevronsUpDown class="icon" />
                </button>
              </div>
              <div v-if="project.canEdit" class="tree-actions">
                <AddMenu
                  @new-document="beginCreateDocument(project.id, null)"
                  @new-folder="beginCreateFolder(project.id, null)"
                />
              </div>
            </div>
          </div>
          <div v-if="project.isExpanded" class="tree-children">
            <DocumentNode
              v-for="document in project.documents"
              :key="document.id"
              :document="document"
              :can-edit="project.canEdit"
              :active-document-id="activeDocumentId"
              @open="openDocument"
              @rename="onRenameDocument"
              @delete="beginDeleteDocument"
              @drag-start="dragStartDocument"
            />
            <FolderNode
              v-for="folder in project.folders"
              :key="folder.id"
              :folder="folder"
              :can-edit="project.canEdit"
              :active-document-id="activeDocumentId"
              @open-document="openDocument"
              @create-document="folder => beginCreateDocument(folder.projectId, folder.id)"
              @create-folder="folder => beginCreateFolder(folder.projectId, folder.id)"
              @rename-folder="onRenameFolder"
              @rename-document="onRenameDocument"
              @delete-folder="beginDeleteFolder"
              @delete-document="beginDeleteDocument"
              @drag-start-folder="dragStartFolder"
              @drag-start-document="dragStartDocument"
              @drop-on-folder="dropOnFolder"
              @toggle-folder="toggleFolder"
            />
            <div v-if="project.canEdit && project.documents.length === 0 && project.folders.length === 0" class="tree-empty">
              Empty - use + to add content
            </div>
          </div>
        </section>
      </nav>
      </template>
      <RouterLink v-else class="login-link" to="/login">Log in to browse</RouterLink>
    </aside>
    <div
      class="sidebar-resizer"
      role="separator"
      aria-label="Resize sidebar"
      aria-orientation="vertical"
      :aria-valuemin="minimumSidebarWidth"
      :aria-valuemax="maximumSidebarWidth"
      :aria-valuenow="sidebarWidth"
      tabindex="0"
      @keydown="resizeSidebarWithKeyboard"
      @pointerdown="beginSidebarResize"
      @pointermove="resizeSidebar"
      @pointerup="finishSidebarResize"
      @pointercancel="finishSidebarResize"
    ></div>
  </div>

  <NameDialog
    v-if="nameDialog"
    :title="dialogTitle"
    :label="dialogLabel"
    :confirm-label="dialogConfirmLabel"
    :initial-value="dialogInitialValue"
    @confirm="confirmName"
    @cancel="closeDialog"
  />
  <ConfirmDialog
    v-if="confirmDialog"
    :title="dialogTitle"
    :message="dialogMessage"
    confirm-label="Delete"
    danger
    @confirm="confirmDelete"
    @cancel="closeDialog"
  />
</template>

<script setup lang="ts">
import { BookOpen, ChevronRight, ChevronsDownUp, ChevronsUpDown, Plus } from 'lucide-vue-next';
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { RouterLink, useRouter } from 'vue-router';
import type { EntityId } from '../api/entityId';
import { PermissionKind } from '../api/generated';
import { getErrorMessage, useSessionStore } from '../stores/session';
import {
  useWorkspaceStore,
  type WorkspaceDocumentTree,
  type WorkspaceFolderTree,
  type WorkspaceProjectTree
} from '../stores/workspace';
import {
  getSidebarScrollTop,
  getSidebarWidth,
  setSidebarScrollTop,
  setSidebarWidth
} from '../stores/workspaceViewport';
import AddMenu from './AddMenu.vue';
import ConfirmDialog from './ConfirmDialog.vue';
import DocumentNode from './DocumentNode.vue';
import FolderNode from './FolderNode.vue';
import NameDialog from './NameDialog.vue';

type DialogKind =
  | 'None'
  | 'CreateProject'
  | 'CreateFolder'
  | 'CreateDocument'
  | 'DeleteFolder'
  | 'DeleteDocument';

const session = useSessionStore();
const workspace = useWorkspaceStore();
const router = useRouter();
const defaultSidebarWidth = 345;
const minimumSidebarWidth = 240;
const sidebarResizeBreakpoint = 1100;
const sidebarKeyboardStep = 16;
const sidebarElement = ref<HTMLElement | null>(null);
const sidebarWidth = ref(defaultSidebarWidth);
const maximumSidebarWidth = ref(calculateMaximumSidebarWidth());
const resizePointerId = ref<number | null>(null);
const dragOverProjectId = ref<EntityId | null>(null);
const draggedDocument = ref<WorkspaceDocumentTree | null>(null);
const draggedFolder = ref<WorkspaceFolderTree | null>(null);
const dialog = reactive<{
  kind: DialogKind;
  projectId: EntityId | null;
  parentFolderId: EntityId | null;
  folder: WorkspaceFolderTree | null;
  document: WorkspaceDocumentTree | null;
}>({
  kind: 'None',
  projectId: null,
  parentFolderId: null,
  folder: null,
  document: null
});

const activeDocumentId = computed(() => workspace.activeDocumentId);
const currentError = computed(() => workspace.error || session.error);
const canCreateProject = computed(() => session.hasGlobalPermission(PermissionKind.CREATE_PROJECT));
const nameDialog = computed(() =>
  ['CreateProject', 'CreateFolder', 'CreateDocument'].includes(dialog.kind)
);
const confirmDialog = computed(() => ['DeleteFolder', 'DeleteDocument'].includes(dialog.kind));
const dialogTitle = computed(() => {
  switch (dialog.kind) {
    case 'CreateProject':
      return 'New project';
    case 'CreateFolder':
      return 'New folder';
    case 'CreateDocument':
      return 'New document';
    case 'DeleteFolder':
      return 'Delete folder';
    case 'DeleteDocument':
      return 'Delete document';
    default:
      return '';
  }
});
const dialogLabel = computed(() => {
  if (dialog.kind === 'CreateProject') {
    return 'Project name';
  }

  if (dialog.kind === 'CreateFolder') {
    return 'Folder name';
  }

  return 'Document title';
});
const dialogConfirmLabel = computed(() => 'Create');
const dialogInitialValue = computed(() => '');
const dialogMessage = computed(() => {
  if (dialog.kind === 'DeleteFolder') {
    return `Delete folder "${dialog.folder?.name}" and all nested content? This cannot be undone.`;
  }

  if (dialog.kind === 'DeleteDocument') {
    return `Delete document "${dialog.document?.title}"? This cannot be undone.`;
  }

  return '';
});

onMounted(async () => {
  window.addEventListener('resize', updateSidebarWidthLimit);
  updateSidebarWidthLimit();
  await restoreSidebarScrollPosition();
  await scrollCurrentActiveDocumentIntoView();
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateSidebarWidthLimit);
  flushSidebarScrollSave();
});

watch(
  () => session.profile?.id,
  userId => {
    sidebarWidth.value = clampSidebarWidth(
      userId == null ? defaultSidebarWidth : getSidebarWidth(userId) ?? defaultSidebarWidth
    );
  },
  { immediate: true }
);

watch(
  () => session.isAuthenticated,
  async isAuthenticated => {
    if (!isAuthenticated) {
      restoredSidebarUserId = null;
      return;
    }

    await restoreSidebarScrollPosition();
  }
);

watch(
  () => workspace.workspace,
  async value => {
    if (value) {
      await restoreSidebarScrollPosition();
      await scrollCurrentActiveDocumentIntoView();
    }
  }
);

watch(
  activeDocumentId,
  async documentId => {
    if (documentId != null) {
      await scrollActiveDocumentIntoView(documentId);
    }
  }
);

let sidebarSaveTimer: number | null = null;
let restoredSidebarUserId: EntityId | null = null;
let restoringSidebarScroll = false;
let resizeStartX = 0;
let resizeStartWidth = defaultSidebarWidth;

function beginSidebarResize(event: PointerEvent): void {
  if (event.button !== 0 || window.innerWidth <= sidebarResizeBreakpoint) {
    return;
  }

  resizePointerId.value = event.pointerId;
  resizeStartX = event.clientX;
  resizeStartWidth = sidebarWidth.value;
  (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  event.preventDefault();
}

function resizeSidebar(event: PointerEvent): void {
  if (resizePointerId.value !== event.pointerId) {
    return;
  }

  sidebarWidth.value = clampSidebarWidth(resizeStartWidth + event.clientX - resizeStartX);
}

function finishSidebarResize(event: PointerEvent): void {
  if (resizePointerId.value !== event.pointerId) {
    return;
  }

  const resizer = event.currentTarget as HTMLElement;
  if (resizer.hasPointerCapture(event.pointerId)) {
    resizer.releasePointerCapture(event.pointerId);
  }

  resizePointerId.value = null;
  persistSidebarWidth();
}

function resizeSidebarWithKeyboard(event: KeyboardEvent): void {
  if (window.innerWidth <= sidebarResizeBreakpoint
    || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) {
    return;
  }

  event.preventDefault();
  const direction = event.key === 'ArrowLeft' ? -1 : 1;
  sidebarWidth.value = clampSidebarWidth(sidebarWidth.value + direction * sidebarKeyboardStep);
  persistSidebarWidth();
}

function persistSidebarWidth(): void {
  const userId = session.profile?.id;
  if (userId != null) {
    setSidebarWidth(userId, sidebarWidth.value);
  }
}

function updateSidebarWidthLimit(): void {
  maximumSidebarWidth.value = calculateMaximumSidebarWidth();
  sidebarWidth.value = clampSidebarWidth(sidebarWidth.value);
}

function clampSidebarWidth(width: number): number {
  return Math.min(maximumSidebarWidth.value, Math.max(minimumSidebarWidth, Math.round(width)));
}

function calculateMaximumSidebarWidth(): number {
  return Math.max(minimumSidebarWidth, Math.floor(window.innerWidth / 2));
}

function scheduleSidebarScrollSave(): void {
  if (restoringSidebarScroll) {
    return;
  }

  if (sidebarSaveTimer != null) {
    window.clearTimeout(sidebarSaveTimer);
  }

  sidebarSaveTimer = window.setTimeout(() => {
    sidebarSaveTimer = null;
    saveSidebarScrollPosition();
  }, 200);
}

function flushSidebarScrollSave(): void {
  if (sidebarSaveTimer != null) {
    window.clearTimeout(sidebarSaveTimer);
    sidebarSaveTimer = null;
  }

  saveSidebarScrollPosition();
}

function saveSidebarScrollPosition(): void {
  const userId = session.profile?.id;
  const element = sidebarElement.value;
  if (userId == null || element == null) {
    return;
  }

  setSidebarScrollTop(userId, element.scrollTop);
}

async function scrollCurrentActiveDocumentIntoView(): Promise<void> {
  const documentId = activeDocumentId.value;
  if (documentId != null) {
    await scrollActiveDocumentIntoView(documentId);
  }
}

async function scrollActiveDocumentIntoView(documentId: EntityId): Promise<void> {
  await nextTick();
  const container = sidebarElement.value;
  if (container == null) {
    return;
  }

  const row = findSidebarDocumentRow(container, documentId);
  if (row == null) {
    return;
  }

  row.scrollIntoView({ block: 'nearest' });
}

function findSidebarDocumentRow(container: HTMLElement, documentId: EntityId): HTMLElement | null {
  const rows = container.querySelectorAll<HTMLElement>('[data-sidebar-document-id]');
  for (const row of rows) {
    if (row.dataset.sidebarDocumentId === documentId) {
      return row;
    }
  }

  return null;
}

async function restoreSidebarScrollPosition(): Promise<void> {
  const userId = session.profile?.id;
  if (userId == null || workspace.workspace == null || restoredSidebarUserId === userId) {
    return;
  }

  await nextTick();
  const element = sidebarElement.value;
  if (element == null) {
    return;
  }

  restoringSidebarScroll = true;
  element.scrollTop = getSidebarScrollTop(userId) ?? 0;
  restoredSidebarUserId = userId;
  window.requestAnimationFrame(() => {
    restoringSidebarScroll = false;
  });
}

async function openDocument(documentId: EntityId): Promise<void> {
  try {
    await router.push({ name: 'workspace-document', params: { documentId: String(documentId) } });
  } catch (error) {
    workspace.error = getErrorMessage(error, 'Unable to open document.');
  }
}

async function toggleProject(project: WorkspaceProjectTree): Promise<void> {
  await workspace.setProjectExpanded(project.id, !project.isExpanded);
}

async function toggleFolder(folder: WorkspaceFolderTree): Promise<void> {
  await workspace.setFolderExpanded(folder.id, !folder.isExpanded);
}

function beginCreateProject(): void {
  openDialog('CreateProject');
}

function beginCreateFolder(projectId: EntityId, parentFolderId: EntityId | null): void {
  dialog.projectId = projectId;
  dialog.parentFolderId = parentFolderId;
  openDialog('CreateFolder');
}

function beginCreateDocument(projectId: EntityId, parentFolderId: EntityId | null): void {
  dialog.projectId = projectId;
  dialog.parentFolderId = parentFolderId;
  openDialog('CreateDocument');
}

async function onRenameFolder(folderId: EntityId, name: string): Promise<void> {
  try {
    await workspace.renameFolder(folderId, name);
  } catch (error) {
    workspace.error = getErrorMessage(error, 'Unable to rename folder.');
  }
}

async function onRenameDocument(documentId: EntityId, title: string): Promise<void> {
  try {
    await workspace.renameDocument(documentId, title);
  } catch (error) {
    workspace.error = getErrorMessage(error, 'Unable to rename document.');
  }
}

function beginDeleteFolder(folder: WorkspaceFolderTree): void {
  dialog.folder = folder;
  openDialog('DeleteFolder');
}

function beginDeleteDocument(document: WorkspaceDocumentTree): void {
  dialog.document = document;
  openDialog('DeleteDocument');
}

function openDialog(kind: DialogKind): void {
  workspace.error = null;
  dialog.kind = kind;
}

function closeDialog(): void {
  dialog.kind = 'None';
  dialog.projectId = null;
  dialog.parentFolderId = null;
  dialog.folder = null;
  dialog.document = null;
}

async function confirmName(name: string): Promise<void> {
  try {
    switch (dialog.kind) {
      case 'CreateProject':
        await workspace.createProject(name);
        break;
      case 'CreateFolder':
        await workspace.createFolder(required(dialog.projectId), dialog.parentFolderId, name);
        break;
      case 'CreateDocument': {
        const documentId = await workspace.createDocument(required(dialog.projectId), dialog.parentFolderId, name);
        await router.push({ name: 'workspace-document', params: { documentId: String(documentId) } });
        break;
      }
    }

    closeDialog();
  } catch (error) {
    workspace.error = getErrorMessage(error, 'Unable to save tree item.');
  }
}

async function confirmDelete(): Promise<void> {
  try {
    if (dialog.kind === 'DeleteFolder') {
      await workspace.deleteFolder(required(dialog.folder?.id));
    } else if (dialog.kind === 'DeleteDocument') {
      await workspace.deleteDocument(required(dialog.document?.id));
    }

    closeDialog();
  } catch (error) {
    workspace.error = getErrorMessage(error, 'Unable to delete item.');
  }
}

function dragStartFolder(folder: WorkspaceFolderTree): void {
  draggedFolder.value = folder;
  draggedDocument.value = null;
}

function dragStartDocument(document: WorkspaceDocumentTree, canMove: boolean): void {
  draggedDocument.value = canMove ? document : null;
  draggedFolder.value = null;
}

async function dropOnFolder(target: WorkspaceFolderTree): Promise<void> {
  await dropInto(target.projectId, target.id);
}

async function dropOnProjectRoot(project: WorkspaceProjectTree): Promise<void> {
  dragOverProjectId.value = null;
  await dropInto(project.id, null);
}

async function dropInto(targetProjectId: EntityId, targetFolderId: EntityId | null): Promise<void> {
  try {
    if (draggedDocument.value) {
      const document = draggedDocument.value;
      clearDrag();
      if (document.projectId !== targetProjectId) {
        workspace.error = 'Cannot move items across projects.';
        return;
      }

      if (document.folderId === targetFolderId) {
        return;
      }

      await workspace.moveDocument(document.id, targetFolderId);
      return;
    }

    if (draggedFolder.value) {
      const folder = draggedFolder.value;
      clearDrag();
      if (folder.projectId !== targetProjectId) {
        workspace.error = 'Cannot move items across projects.';
        return;
      }

      if (folder.id === targetFolderId || folder.parentFolderId === targetFolderId) {
        return;
      }

      await workspace.moveFolder(folder.id, targetFolderId);
    }
  } catch (error) {
    workspace.error = getErrorMessage(error, 'Unable to move item.');
  }
}

function clearDrag(): void {
  draggedDocument.value = null;
  draggedFolder.value = null;
  dragOverProjectId.value = null;
}

function required(value: EntityId | null | undefined): EntityId {
  if (value == null) {
    throw new Error('Required value was missing.');
  }

  return value;
}
</script>
